import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Sparkles, AlertTriangle, Wrench, CheckCircle2, ArrowRight, Gauge } from "lucide-react";
import localApi from "@/lib/localApi";
import clientApi, { clientToken } from "@/lib/clientApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

const MAKES = ["Acura", "Audi", "BMW", "Buick", "Cadillac", "Chevrolet", "Chrysler", "Dodge", "Ford", "GMC",
  "Honda", "Hyundai", "Infiniti", "Jeep", "Kia", "Lexus", "Mazda", "Mercedes-Benz", "Nissan", "Ram",
  "Subaru", "Tesla", "Toyota", "Volkswagen", "Volvo", "Other"];
const NOW = new Date().getFullYear();
const YEARS = Array.from({ length: NOW - 1989 }, (_, i) => String(NOW - i));

const URGENCY = {
  High: "text-rose-400 bg-rose-400/10 border-rose-400/20",
  Medium: "text-amber-400 bg-amber-400/10 border-amber-400/20",
  Low: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
};
const field = "h-10 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50";

export function DiagnoseFlow({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [step, setStep] = useState("form");
  const [busy, setBusy] = useState(false);
  const [veh, setVeh] = useState({ vehicle_year: "", vehicle_make: "", vehicle_model: "" });
  const [symptoms, setSymptoms] = useState("");
  const [report, setReport] = useState(null);
  const [acct, setAcct] = useState({ name: "", email: "", password: "", phone: "", zip_code: "" });
  const setA = (k) => (e) => setAcct((a) => ({ ...a, [k]: e.target.value }));

  const reset = (o) => { if (!o) { setStep("form"); setReport(null); setBusy(false); } onOpenChange(o); };

  const diagnose = async () => {
    if (!symptoms.trim()) return toast.error("Please describe the symptoms");
    setBusy(true);
    try {
      const { data } = await localApi.post("/marketplace/diagnose", { ...veh, symptoms });
      setReport(data);
      setStep("report");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate diagnosis"); }
    finally { setBusy(false); }
  };

  const postJob = async () => {
    if (!acct.name.trim() || !acct.phone.trim()) return toast.error("Add your name and phone");
    if (!acct.email.trim() || acct.password.length < 6) return toast.error("Add email and a password (6+ chars)");
    if (!acct.zip_code.trim()) return toast.error("Add your ZIP so we can match nearby mechanics");
    setBusy(true);
    try {
      const { data } = await clientApi.post("/clients/register", { email: acct.email, password: acct.password, name: acct.name });
      clientToken.set(data.token);
      await localApi.post("/marketplace/requests", {
        name: acct.name, phone: acct.phone, email: acct.email, zip_code: acct.zip_code,
        ...veh, issue: symptoms,
        ai_summary: report?.summary || "", ai_cost_low: report?.cost_low, ai_cost_high: report?.cost_high,
      });
      setStep("done");
      toast.success("Job posted to the marketplace!");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create account / post job"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-lg max-h-[92vh] overflow-y-auto" data-testid="diagnose-dialog">
        {step === "form" && (
          <div data-testid="diagnose-step-form">
            <DialogHeader>
              <DialogTitle className="text-slate-100 font-head flex items-center gap-2"><Sparkles className="h-5 w-5 text-amber-400" /> Instant AI Diagnosis</DialogTitle>
              <DialogDescription className="text-slate-500">Tell us your vehicle and symptoms — get a free diagnostic report in seconds. No login needed.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-xs">Year</Label>
                  <Select value={veh.vehicle_year} onValueChange={(v) => setVeh((s) => ({ ...s, vehicle_year: v }))}>
                    <SelectTrigger data-testid="diag-year" className={field}><SelectValue placeholder="Year" /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700 max-h-60">{YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-slate-300 text-xs">Make</Label>
                  <Select value={veh.vehicle_make} onValueChange={(v) => setVeh((s) => ({ ...s, vehicle_make: v }))}>
                    <SelectTrigger data-testid="diag-make" className={field}><SelectValue placeholder="Select make" /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700 max-h-60">{MAKES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Model</Label><Input data-testid="diag-model" value={veh.vehicle_model} onChange={(e) => setVeh((s) => ({ ...s, vehicle_model: e.target.value }))} placeholder="e.g. Camry" className={field} /></div>
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Symptoms *</Label><Textarea data-testid="diag-symptoms" value={symptoms} onChange={(e) => setSymptoms(e.target.value)} placeholder="Describe the noise, warning light, or problem…" className="bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50 min-h-24" /></div>
            </div>
            <Button data-testid="diag-run" onClick={diagnose} disabled={busy} className="w-full h-11 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} Get Instant AI Diagnosis
            </Button>
          </div>
        )}

        {step === "report" && report && (
          <div data-testid="diagnose-step-report">
            <DialogHeader>
              <DialogTitle className="text-slate-100 font-head flex items-center gap-2"><Gauge className="h-5 w-5 text-amber-400" /> AI Diagnostic Report</DialogTitle>
              <DialogDescription className="text-slate-500">{report.vehicle}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-3">
              <div className="flex items-start gap-3">
                <span data-testid="diag-urgency" className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 py-1 border shrink-0 ${URGENCY[report.urgency] || URGENCY.Medium}`}>
                  <AlertTriangle className="h-3.5 w-3.5" /> {report.urgency} urgency
                </span>
                <p className="text-sm text-slate-300">{report.summary}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">Potential causes</p>
                <div className="space-y-2" data-testid="diag-causes">
                  {(report.potential_causes || []).map((c, i) => (
                    <div key={`${c.name || "cause"}-${i}`} className="rounded-lg border border-slate-800 bg-slate-950/50 p-3">
                      <p className="text-sm font-medium text-slate-100 flex items-center gap-2"><Wrench className="h-3.5 w-3.5 text-amber-400" /> {c.name}</p>
                      {c.detail && <p className="text-xs text-slate-400 mt-1">{c.detail}</p>}
                    </div>
                  ))}
                </div>
              </div>
              {(report.labor_hours_low != null) && (
                <p className="text-xs text-slate-500">Estimated labor: {report.labor_hours_low}–{report.labor_hours_high} hrs</p>
              )}
              {/* Conversion hook */}
              <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4" data-testid="diag-conversion-hook">
                <p className="text-sm text-slate-200"><b>Marketplace Estimate:</b> Based on this diagnosis, local mechanics can fix this for <span className="font-bold text-amber-400">${report.cost_low}–${report.cost_high}</span>.</p>
                <Button data-testid="diag-post-job" onClick={() => setStep("signup")} className="w-full mt-3 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
                  Post Job & Get Bids <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </div>
              <button data-testid="diag-back" onClick={() => setStep("form")} className="text-xs text-slate-500 hover:text-slate-300">← Start over</button>
            </div>
          </div>
        )}

        {step === "signup" && (
          <div data-testid="diagnose-step-signup">
            <DialogHeader>
              <DialogTitle className="text-slate-100 font-head">Post your job & get bids</DialogTitle>
              <DialogDescription className="text-slate-500">Create a free account so local mechanics can bid — and you can track responses.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Your name *</Label><Input data-testid="signup-name" value={acct.name} onChange={setA("name")} className={field} /></div>
                <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Phone *</Label><Input data-testid="signup-phone" value={acct.phone} onChange={setA("phone")} className={field} /></div>
              </div>
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Email *</Label><Input data-testid="signup-email" type="email" value={acct.email} onChange={setA("email")} className={field} /></div>
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Password *</Label><Input data-testid="signup-password" type="password" value={acct.password} onChange={setA("password")} placeholder="6+ characters" className={field} /></div>
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">ZIP code *</Label><Input data-testid="signup-zip" value={acct.zip_code} onChange={setA("zip_code")} placeholder="e.g. 90210" className={field} /></div>
            </div>
            <Button data-testid="signup-post" onClick={postJob} disabled={busy} className="w-full h-11 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
              {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Create Account & Post Job
            </Button>
            <button data-testid="signup-back" onClick={() => setStep("report")} className="text-xs text-slate-500 hover:text-slate-300 mt-2 block mx-auto">← Back to report</button>
          </div>
        )}

        {step === "done" && (
          <div className="py-6 text-center" data-testid="diagnose-step-done">
            <div className="h-14 w-14 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="h-7 w-7 text-emerald-400" /></div>
            <h3 className="font-head font-bold text-xl text-slate-50">Your job is live!</h3>
            <p className="text-slate-400 mt-2 max-w-sm mx-auto">Local mechanics near {acct.zip_code} are being notified. Track their bids from your dashboard.</p>
            <Button data-testid="done-dashboard" onClick={() => navigate("/client")} className="mt-6 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">Go to my dashboard</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
